import React from 'react';
import axios from 'axios';
import CongestionChart from '../pages/CongestionChart';
import '../style/components/NetworkCongestionPanel.scss';

const HOST_PORT = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  ? 'http://localhost:3003' : 'https://api.blockhivetechnologies.com';

const POLL_MS = 60 * 60 * 1000; // hourly

class NetworkCongestionPanel extends React.Component {
  state = { data: null, history: null, seasonality: null, error: null };
  _mounted = false;
  _timer = null;

  componentDidMount() {
    this._mounted = true;
    this.fetchData();
    this._timer = setInterval(() => { if (!document.hidden) this.fetchData(); }, POLL_MS);
  }
  componentWillUnmount() {
    this._mounted = false;
    if (this._timer) clearInterval(this._timer);
  }

  fetchData = async () => {
    try {
      // Seasonality fetch disabled with the panel — re-add to Promise.all when re-enabling:
      // axios.get(`${HOST_PORT}/network/congestion/seasonality`, { timeout: 30000 }).catch(() => null)
      const [snap, hist] = await Promise.all([
        axios.get(`${HOST_PORT}/network/congestion`, { timeout: 30000 }),
        axios.get(`${HOST_PORT}/network/congestion/history`, { timeout: 30000 }),
      ]);
      if (!this._mounted) return;
      this.setState({ data: snap.data, history: hist.data, error: null });
    } catch (e) {
      if (!this._mounted) return;
      this.setState({ error: e.message });
    }
  }

  render() {
    const { data, history, error } = this.state;
    return (
      <div className="ncp">
        <div className="ncp-titlebar">
          <div className="ncp-title">NETWORK CONGESTION</div>
          <div className="ncp-sub">CURRENT vs 90D BASELINE</div>
        </div>
        <div className="ncp-body">
          {history && history.btc ? (
            <>
              <CongestionChart
                data={history.btc}
                unit="sat/vB"
                chainStates={data}
              />
              {/* FeeSeasonality hidden — re-enable by restoring: {seasonality && <FeeSeasonality seas={seasonality} />} */}
            </>
          ) : error ? (
            <div className="ncp-state ncp-error">Failed: {error}</div>
          ) : (
            <div className="ncp-state">Loading…</div>
          )}
        </div>
      </div>
    );
  }
}

// ─── Fee seasonality: send-now-or-wait strip + day×hour heatmap (ET) ─────────
const ORB = { fontFamily: 'Orbitron, sans-serif' };
const ET_DAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

// DST-aware ET offset via the project-standard noon probe
function etOffsetHours() {
  const noonUTC = new Date(); noonUTC.setUTCHours(12, 0, 0, 0);
  const etHour = parseInt(new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', hour: 'numeric', hour12: false,
  }).format(noonUTC), 10);
  return 12 - etHour; // 4 = EDT, 5 = EST
}

function relColor(t) {
  // t 0 (cheap) → 1 (expensive): green → yellow → red
  const h = Math.round(150 - t * 150);
  return `hsl(${h}, 62%, ${38 + (1 - t) * 8}%)`;
}

function fmtWindow(startTs, endTs) {
  const opt = { timeZone: 'America/New_York', weekday: 'short', hour: 'numeric' };
  const f = ts => new Date(ts * 1000).toLocaleString('en-US', opt).replace(',', '').toUpperCase();
  return `${f(startTs)} – ${new Date(endTs * 1000).toLocaleString('en-US', { timeZone: 'America/New_York', hour: 'numeric' }).toUpperCase()} ET`;
}

// eslint-disable-next-line no-unused-vars
const FeeSeasonality = ({ seas }) => {
  const { grid, current, best_window, savings, unit } = seas;
  if (!grid) return null;

  const offset = etOffsetHours();
  const nowEt = new Date(Date.now() - offset * 3600000);
  const nowEtDow = nowEt.getUTCDay(), nowEtHour = nowEt.getUTCHours();

  // Color scale over the rel values present in the grid
  const rels = grid.flat().map(c => c.rel).filter(r => r != null);
  const rMin = Math.min(...rels), rMax = Math.max(...rels);
  const span = rMax - rMin || 1;

  // ET cell → UTC cell
  const cellAt = (etDow, etHour) => {
    const utcTotal = etHour + offset;
    const utcDow = (etDow + Math.floor(utcTotal / 24)) % 7;
    return grid[utcDow][utcTotal % 24];
  };

  const sendNow = savings && savings.save_pct != null && savings.save_pct <= 5;

  return (
    <div style={{ marginTop: 10 }}>
      {/* Recommendation strip */}
      {current && best_window && (
        <div style={{
          ...ORB, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          border: `1px solid ${sendNow ? '#26c49a' : '#333'}`,
          background: sendNow ? 'rgba(38,196,154,0.08)' : 'rgba(255,255,255,0.02)',
          borderRadius: 2, padding: '5px 10px', marginBottom: 8, fontSize: 9,
        }}>
          <span style={{ color: '#ddd', fontWeight: 700, letterSpacing: '1px' }}>
            NOW {current.fee} {unit}
          </span>
          {current.vs_typical_pct != null && (
            <span style={{ color: current.vs_typical_pct > 10 ? '#ff4d4d' : current.vs_typical_pct < -10 ? '#26c49a' : '#888' }}>
              {current.vs_typical_pct >= 0 ? '+' : ''}{current.vs_typical_pct}% vs typical this hour
            </span>
          )}
          <span style={{ color: '#555' }}>·</span>
          {sendNow ? (
            <span style={{ color: '#26c49a', fontWeight: 700, letterSpacing: '0.8px' }}>
              GOOD TIME TO SEND — near the 48H low
            </span>
          ) : (
            <>
              <span style={{ color: '#888' }}>
                CHEAPEST NEXT 48H: <span style={{ color: '#26c49a', fontWeight: 700 }}>{fmtWindow(best_window.start_ts, best_window.end_ts)}</span>
                {' '}(~{best_window.expected_fee} {unit})
              </span>
              {savings && savings.save_usd > 0 && (
                <span style={{ color: '#f7c948' }}>
                  WAIT SAVES ~${savings.save_usd} ({savings.save_pct}%) / tx
                </span>
              )}
            </>
          )}
        </div>
      )}

      {/* Day × hour heatmap, ET */}
      <div style={{ ...ORB, fontSize: 7, color: '#666', letterSpacing: '1px', marginBottom: 3 }}>
        FEE SEASONALITY · 90D · TYPICAL BY HOUR (ET)
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '26px repeat(24, 1fr)', gap: 1 }}>
        {ET_DAYS.map((day, etDow) => (
          <React.Fragment key={day}>
            <div style={{ ...ORB, fontSize: 7, color: '#777', alignSelf: 'center', letterSpacing: '0.5px' }}>{day}</div>
            {Array.from({ length: 24 }, (_, etHour) => {
              const cell = cellAt(etDow, etHour);
              const isNow = etDow === nowEtDow && etHour === nowEtHour;
              const t = cell.rel != null ? (cell.rel - rMin) / span : null;
              return (
                <div
                  key={etHour}
                  title={`${day} ${etHour}:00 ET · typ ${cell.median != null ? cell.median : '—'} ${unit}`}
                  style={{
                    height: 9,
                    background: t != null ? relColor(t) : '#181818',
                    outline: isNow ? '1px solid #37E6CF' : 'none',
                    outlineOffset: isNow ? 0 : undefined,
                    borderRadius: 1,
                  }}
                />
              );
            })}
          </React.Fragment>
        ))}
        {/* Hour axis */}
        <div />
        {Array.from({ length: 24 }, (_, h) => (
          <div key={h} style={{ ...ORB, fontSize: 6, color: '#555', textAlign: 'center' }}>
            {h % 4 === 0 ? h : ''}
          </div>
        ))}
      </div>
    </div>
  );
};

export default NetworkCongestionPanel;
